import { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { Instagram, Search, Trash2, CheckCircle2, AlertTriangle, Inbox, Loader2, Menu, Lock, Unlock } from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';
import {
  getSuperAdminAccounts,
  deleteSocialAccount,
  bulkDeleteSocialAccounts,
  lockAccounts,
  unlockAccounts,
  SuperAdminAccount,
  SuperAdminAccountsResponse
} from 'services/admin/superAdminService';
import Spinner from 'components/common/Spinner';

export default function AccountsManagement() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1E293B' : '#E2E8F0';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#1E293B';
  const paginationBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBorder = isDark ? '#314158' : '#D1D5DB';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const successBg = isDark ? '#0D2818' : '#F0FDF4';
  const successBorder = isDark ? '#1A4028' : '#BBF7D0';
  const successText = isDark ? '#5EE9B5' : '#166534';
  const errorBg = isDark ? '#2A1215' : '#FEF2F2';
  const errorBorder = isDark ? '#4A1C20' : '#FECACA';
  const errorText = isDark ? '#F87171' : '#991B1B';
  const modalBackdrop = isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  const modalBg = isDark ? '#1D293D' : '#FFFFFF';
  const modalInfoBg = isDark ? '#111827' : '#F9FAFB';

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Accounts state
  const [accounts, setAccounts] = useState<SuperAdminAccount[]>([]);

  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Lock/Unlock modal states
  const [showLockModal, setShowLockModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showBulkLockModal, setShowBulkLockModal] = useState(false);
  const [showBulkUnlockModal, setShowBulkUnlockModal] = useState(false);
  const [accountToLock, setAccountToLock] = useState<string | null>(null);
  const [accountToUnlock, setAccountToUnlock] = useState<string | null>(null);
  const [lockTitleEn, setLockTitleEn] = useState('');
  const [lockTitleFr, setLockTitleFr] = useState('');
  const [lockDescEn, setLockDescEn] = useState('');
  const [lockDescFr, setLockDescFr] = useState('');

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response: SuperAdminAccountsResponse = await getSuperAdminAccounts({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        platform: platformFilter || undefined
      });
      setAccounts(response.accounts || []);
      setPagination({
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err);
      setError(err.message || 'Failed to fetch accounts');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, platformFilter]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      setActionLoading(true);
      await deleteSocialAccount(accountToDelete, permanentDelete);
      setSuccessMessage(permanentDelete ? 'Account permanently deleted' : 'Account deleted successfully');
      setShowDeleteModal(false);
      setAccountToDelete(null);
      setPermanentDelete(false);
      fetchAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedAccounts.length === 0) return;

    try {
      setActionLoading(true);
      const response = await bulkDeleteSocialAccounts(selectedAccounts, permanentDelete);
      setSuccessMessage(`${response.deleted} accounts deleted successfully`);
      setShowBulkDeleteModal(false);
      setSelectedAccounts([]);
      setPermanentDelete(false);
      fetchAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Reset lock form
  const resetLockForm = () => {
    setLockTitleEn('');
    setLockTitleFr('');
    setLockDescEn('');
    setLockDescFr('');
  };

  // Handle lock account
  const handleLockAccount = async () => {
    if (!accountToLock) return;
    if (!lockTitleEn || !lockTitleFr || !lockDescEn || !lockDescFr) return;

    try {
      setActionLoading(true);
      const response = await lockAccounts({
        account_ids: [accountToLock],
        title: { en: lockTitleEn, fr: lockTitleFr },
        description: { en: lockDescEn, fr: lockDescFr }
      });
      setSuccessMessage(t('superAdmin.accountsManagement.lockSuccess', { count: response.modifiedCount }) || `${response.modifiedCount} account(s) locked successfully`);
      setShowLockModal(false);
      setAccountToLock(null);
      resetLockForm();
      fetchAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle unlock account
  const handleUnlockAccount = async () => {
    if (!accountToUnlock) return;

    try {
      setActionLoading(true);
      const response = await unlockAccounts({ account_ids: [accountToUnlock] });
      setSuccessMessage(t('superAdmin.accountsManagement.unlockSuccess', { count: response.modifiedCount }) || `${response.modifiedCount} account(s) unlocked successfully`);
      setShowUnlockModal(false);
      setAccountToUnlock(null);
      fetchAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk lock
  const handleBulkLock = async () => {
    if (selectedAccounts.length === 0) return;
    if (!lockTitleEn || !lockTitleFr || !lockDescEn || !lockDescFr) return;

    try {
      setActionLoading(true);
      const response = await lockAccounts({
        account_ids: selectedAccounts,
        title: { en: lockTitleEn, fr: lockTitleFr },
        description: { en: lockDescEn, fr: lockDescFr }
      });
      setSuccessMessage(t('superAdmin.accountsManagement.lockSuccess', { count: response.modifiedCount }) || `${response.modifiedCount} account(s) locked successfully`);
      setShowBulkLockModal(false);
      setSelectedAccounts([]);
      resetLockForm();
      fetchAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk unlock
  const handleBulkUnlock = async () => {
    if (selectedAccounts.length === 0) return;

    try {
      setActionLoading(true);
      const response = await unlockAccounts({ account_ids: selectedAccounts });
      setSuccessMessage(t('superAdmin.accountsManagement.unlockSuccess', { count: response.modifiedCount }) || `${response.modifiedCount} account(s) unlocked successfully`);
      setShowBulkUnlockModal(false);
      setSelectedAccounts([]);
      fetchAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle select all accounts
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAccounts(accounts.map(account => account._id));
    } else {
      setSelectedAccounts([]);
    }
  };

  // Handle individual select
  const handleSelectAccount = (accountId: string, checked: boolean) => {
    if (checked) {
      setSelectedAccounts([...selectedAccounts, accountId]);
    } else {
      setSelectedAccounts(selectedAccounts.filter(id => id !== accountId));
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format next job time
  const formatNextJobTime = (dateString: string | null) => {
    if (!dateString) return 'None scheduled';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Delete Modal
  const deleteModal = showDeleteModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: errorBg }}>
            <AlertTriangle size={20} style={{ color: errorText }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Delete Account</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          Are you sure you want to delete this social media account? This action {permanentDelete ? 'cannot be undone' : 'may be reversible'}.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="permanentDeleteAccount"
            checked={permanentDelete}
            onChange={(e) => setPermanentDelete(e.target.checked)}
            className="rounded text-red-600 focus:ring-red-500"
            style={{ borderColor: inputBorder }}
          />
          <label htmlFor="permanentDeleteAccount" className="text-sm" style={{ color: subtitleColor }}>
            Permanently delete (cannot be recovered)
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setAccountToDelete(null);
              setPermanentDelete(false);
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ background: modalInfoBg, color: titleColor }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            disabled={actionLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Bulk Delete Modal
  const bulkDeleteModal = showBulkDeleteModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: errorBg }}>
            <AlertTriangle size={20} style={{ color: errorText }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Bulk Delete Accounts</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          Are you sure you want to delete {selectedAccounts.length} social media accounts? This action {permanentDelete ? 'cannot be undone' : 'may be reversible'}.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="bulkPermanentDeleteAccount"
            checked={permanentDelete}
            onChange={(e) => setPermanentDelete(e.target.checked)}
            className="rounded text-red-600 focus:ring-red-500"
            style={{ borderColor: inputBorder }}
          />
          <label htmlFor="bulkPermanentDeleteAccount" className="text-sm" style={{ color: subtitleColor }}>
            Permanently delete (cannot be recovered)
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowBulkDeleteModal(false);
              setPermanentDelete(false);
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ background: modalInfoBg, color: titleColor }}
          >
            Cancel
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={actionLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            Delete All
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Lock form fields component (reused in single and bulk lock modals)
  const lockFormFields = (
    <div className="flex flex-col gap-3 mb-4">
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.lockTitleEn', 'Title (English)')} *
        </label>
        <input
          type="text"
          value={lockTitleEn}
          onChange={(e) => setLockTitleEn(e.target.value)}
          placeholder="e.g. Account Suspended"
          className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
        />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.lockTitleFr', 'Title (French)')} *
        </label>
        <input
          type="text"
          value={lockTitleFr}
          onChange={(e) => setLockTitleFr(e.target.value)}
          placeholder="ex. Compte Suspendu"
          className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
        />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.lockDescEn', 'Description (English)')} *
        </label>
        <textarea
          value={lockDescEn}
          onChange={(e) => setLockDescEn(e.target.value)}
          placeholder="Reason for locking this account..."
          rows={2}
          className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
          style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
        />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.lockDescFr', 'Description (French)')} *
        </label>
        <textarea
          value={lockDescFr}
          onChange={(e) => setLockDescFr(e.target.value)}
          placeholder="Raison du verrouillage de ce compte..."
          rows={2}
          className="w-full px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
          style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
        />
      </div>
    </div>
  );

  const lockFormValid = lockTitleEn.trim() && lockTitleFr.trim() && lockDescEn.trim() && lockDescFr.trim();

  // Lock Modal (single account)
  const lockModal = showLockModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#422006' : '#FEF3C7' }}>
            <Lock size={20} style={{ color: isDark ? '#FBBF24' : '#D97706' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>
            {t('superAdmin.accountsManagement.lockAccount', 'Lock Account')}
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.lockConfirmSingle', 'This will lock the account and prevent all automated actions.')}
        </p>
        {lockFormFields}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowLockModal(false);
              setAccountToLock(null);
              resetLockForm();
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ background: modalInfoBg, color: titleColor }}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleLockAccount}
            disabled={actionLoading || !lockFormValid}
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ background: '#D97706' }}
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            {t('superAdmin.accountsManagement.lock', 'Lock')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Bulk Lock Modal
  const bulkLockModal = showBulkLockModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#422006' : '#FEF3C7' }}>
            <Lock size={20} style={{ color: isDark ? '#FBBF24' : '#D97706' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>
            {t('superAdmin.accountsManagement.lockAccounts', 'Lock Accounts')}
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.lockConfirmBulk', { count: selectedAccounts.length }) || `This will lock ${selectedAccounts.length} accounts and prevent all automated actions.`}
        </p>
        {lockFormFields}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowBulkLockModal(false);
              resetLockForm();
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ background: modalInfoBg, color: titleColor }}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleBulkLock}
            disabled={actionLoading || !lockFormValid}
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ background: '#D97706' }}
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            {t('superAdmin.accountsManagement.lockSelected', 'Lock Selected')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Unlock Modal (single account)
  const unlockModal = showUnlockModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#0D2818' : '#DCFCE7' }}>
            <Unlock size={20} style={{ color: isDark ? '#5EE9B5' : '#15803D' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>
            {t('superAdmin.accountsManagement.unlockAccount', 'Unlock Account')}
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.unlockConfirmSingle', 'This will unlock the account and allow automated actions to resume.')}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowUnlockModal(false);
              setAccountToUnlock(null);
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ background: modalInfoBg, color: titleColor }}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleUnlockAccount}
            disabled={actionLoading}
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ background: '#15803D' }}
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            {t('superAdmin.accountsManagement.unlock', 'Unlock')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Bulk Unlock Modal
  const bulkUnlockModal = showBulkUnlockModal ? ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isDark ? '#0D2818' : '#DCFCE7' }}>
            <Unlock size={20} style={{ color: isDark ? '#5EE9B5' : '#15803D' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>
            {t('superAdmin.accountsManagement.unlockAccounts', 'Unlock Accounts')}
          </h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          {t('superAdmin.accountsManagement.unlockConfirmBulk', { count: selectedAccounts.length }) || `This will unlock ${selectedAccounts.length} accounts and allow automated actions to resume.`}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setShowBulkUnlockModal(false)}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ background: modalInfoBg, color: titleColor }}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleBulkUnlock}
            disabled={actionLoading}
            className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ background: '#15803D' }}
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            {t('superAdmin.accountsManagement.unlockSelected', 'Unlock Selected')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ gap: '8px', background: containerBg }}>

      {/* Main Card */}
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}>

        {/* Header - 84px */}
        <div className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{ padding: '12px 16px', borderBottom: `1px solid ${headerBorder}` }}>
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            {/* Hamburger menu for mobile */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: isDark ? '#1C398E' : '#EFF6FF', borderRadius: '12px', alignItems: 'center', justifyContent: 'center' }}>
              <Instagram size={20} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
            </div>
            <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0, flex: 1 }}>
              {t('superAdmin.accountsManagement.title', 'Accounts Management')}
            </h1>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', flex: 1, minHeight: 0 }}>

          {/* Messages */}
          {successMessage && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: successBg, borderWidth: 1, borderStyle: 'solid', borderColor: successBorder, color: successText }}>
              <CheckCircle2 size={16} />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="p-3 rounded-lg text-sm flex items-center gap-2" style={{ background: errorBg, borderWidth: 1, borderStyle: 'solid', borderColor: errorBorder, color: errorText }}>
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {/* Search and Filters (no card wrapper) */}
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <div className="flex flex-1 gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: subtitleColor }}>
                    <Search size={14} />
                  </span>
                  <input
                    type="text"
                    placeholder={t('superAdmin.search.placeholder', 'Search accounts...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
              >
                <option value="">All Platforms</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
                <option value="twitter">Twitter</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>
            {selectedAccounts.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setShowBulkLockModal(true)}
                  className="px-4 py-2 text-white text-sm font-medium rounded-md transition-colors flex items-center gap-2"
                  style={{ background: '#D97706' }}
                >
                  <Lock size={14} />
                  {t('superAdmin.accountsManagement.lockSelected', 'Lock Selected')} ({selectedAccounts.length})
                </button>
                <button
                  onClick={() => setShowBulkUnlockModal(true)}
                  className="px-4 py-2 text-white text-sm font-medium rounded-md transition-colors flex items-center gap-2"
                  style={{ background: '#15803D' }}
                >
                  <Unlock size={14} />
                  {t('superAdmin.accountsManagement.unlockSelected', 'Unlock Selected')} ({selectedAccounts.length})
                </button>
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <Trash2 size={14} />
                  Delete Selected ({selectedAccounts.length})
                </button>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && <Spinner size="large" text={t('superAdmin.loading', 'Loading accounts...')} fullPage />}

          {/* Accounts Table (with border and overflow-hidden) */}
          {!loading && (
            <div className={`overflow-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ borderRadius: '8px', border: `1px solid ${tableBorderColor}` }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: tableHeaderBg, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.length === accounts.length && accounts.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded text-purple-600 focus:ring-purple-500"
                          style={{ borderColor: inputBorder }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Account</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Owner</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Platform</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>{t('superAdmin.accountsManagement.locked', 'Locked')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Jobs</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Next Job</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Created</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody style={{ borderColor: tableBorderColor }}>
                    {accounts.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center" style={{ color: subtitleColor }}>
                          <Inbox size={28} className="mx-auto mb-2" />
                          No accounts found
                        </td>
                      </tr>
                    ) : (
                      accounts.map((account) => (
                        <tr key={account._id} style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedAccounts.includes(account._id)}
                              onChange={(e) => handleSelectAccount(account._id, e.target.checked)}
                              className="rounded text-purple-600 focus:ring-purple-500"
                              style={{ borderColor: inputBorder }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: isDark ? '#2A1225' : '#FCE7F3' }}>
                                <Instagram size={16} style={{ color: isDark ? '#F9A8D4' : '#DB2777' }} />
                              </div>
                              <div>
                                <span className="text-sm font-medium" style={{ color: titleColor }}>@{account.username}</span>
                                {account.full_name && (
                                  <p className="text-xs" style={{ color: subtitleColor }}>{account.full_name}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium" style={{ color: titleColor }}>{account.user?.fullName || 'Unknown'}</p>
                              <p className="text-xs" style={{ color: subtitleColor }}>{account.user?.email || ''}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded-full capitalize" style={{ background: isDark ? '#2A1225' : '#FCE7F3', color: isDark ? '#F9A8D4' : '#BE185D' }}>
                              {account.platform}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="px-2 py-1 text-xs font-medium rounded-full"
                              style={account.is_active
                                ? { background: isDark ? '#0D2818' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' }
                                : { background: isDark ? '#1D293D' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' }
                              }
                            >
                              {account.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {account.is_locked ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full inline-flex items-center gap-1" style={{ background: isDark ? '#422006' : '#FEF3C7', color: isDark ? '#FBBF24' : '#D97706' }}>
                                <Lock size={10} />
                                {t('superAdmin.accountsManagement.locked', 'Locked')}
                              </span>
                            ) : (
                              <span className="text-xs" style={{ color: subtitleColor }}>—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span style={{ color: isDark ? '#FACC15' : '#CA8A04' }}>{account.pendingJobs}</span>
                            <span style={{ color: subtitleColor }}> / </span>
                            <span style={{ color: subtitleColor }}>{account.totalJobs}</span>
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>{formatNextJobTime(account.next_job_time)}</td>
                          <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{formatDate(account.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {account.is_locked ? (
                                <button
                                  onClick={() => {
                                    setAccountToUnlock(account._id);
                                    setShowUnlockModal(true);
                                  }}
                                  className="transition-colors"
                                  style={{ color: isDark ? '#5EE9B5' : '#15803D' }}
                                  title={t('superAdmin.accountsManagement.unlock', 'Unlock') || 'Unlock'}
                                >
                                  <Unlock size={14} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setAccountToLock(account._id);
                                    setShowLockModal(true);
                                  }}
                                  className="transition-colors"
                                  style={{ color: isDark ? '#FBBF24' : '#D97706' }}
                                  title={t('superAdmin.accountsManagement.lock', 'Lock') || 'Lock'}
                                >
                                  <Lock size={14} />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setAccountToDelete(account._id);
                                  setShowDeleteModal(true);
                                }}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Delete Account"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
                <div className="text-sm" style={{ color: subtitleColor }}>
                  Showing {pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} accounts
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals via portals */}
      {deleteModal}
      {bulkDeleteModal}
      {lockModal}
      {bulkLockModal}
      {unlockModal}
      {bulkUnlockModal}
    </div>
  );
}
